/**
 * A2P SMS / automated-voice consent copy — must match backend/sessions/communication_consent.py
 * (version 2026-06-16-a2p-v1). Used on form_13 and cross-site comms-consent UI.
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';

export const A2P_CONSENT_VERSION = '2026-06-16-a2p-v1';

/** Parent-facing / consent copy */
export const LEGAL_ENTITY_NAME = 'Hebrew Academy for Special Children, Inc.';
/** Carrier / 10DLC registration (IRS format — verified 2026-06-16) */
export const LEGAL_ENTITY_IRS_NAME = 'HEBREW ACADEMY FOR SPECIAL CHILDREN INC';
export const LEGAL_ENTITY_EIN = '11-2107335';

export const SMS_CONSENT_LABEL =
  'I agree to receive text messages from Hebrew Academy for Special Children, Inc. (HASC) '
  + 'at the mobile phone number(s) I provided, about my child\'s program and school operations, '
  + 'such as schedule changes, closures, emergencies, transportation, attendance, '
  + 'health-related reminders, and other school reminders. Message frequency varies. '
  + 'Message and data rates may apply. Reply STOP to opt out of texts or HELP for help. '
  + 'Consent is not a condition of enrollment or services, and I may revoke consent at any time.';

export const VOICE_CONSENT_LABEL =
  'I agree to receive phone calls from Hebrew Academy for Special Children, Inc. (HASC) '
  + 'at the phone number(s) I provided, including calls made using an automated, prerecorded, '
  + 'artificial, or AI-generated voice, about my child\'s program and school operations, '
  + 'such as schedule changes, closures, emergencies, transportation, attendance, '
  + 'health-related reminders, and other school reminders. Consent is not a condition of '
  + 'enrollment or services, and I may revoke consent at any time by contacting the office.';

export const ALTERNATE_CHANNEL_NOTICE =
  'Required school notices may also be sent by email, phone, or mailed letter using contact '
  + 'information you have provided, regardless of whether you opt in to text messages or '
  + 'automated calls above.';

/** Monitored main-office HELP / opt-out line (confirmed 2026-06-16) */
export const HASC_HELP_PHONE = '718-686-5900';

export const SENSITIVE_CONTENT_GUARDRAIL =
  'Operational school communications only. Do not send detailed medical, clinical, IEP, '
  + 'disciplinary, or other sensitive student information via SMS or automated/AI voice. '
  + 'For sensitive matters, ask the parent to contact the office.';

/**
 * Optional SMS + voice consent checkboxes for form_13 (both default unchecked).
 */
export function ParentPacketCommunicationConsentFields({ fields, setF }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Text messages and automated calls (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        You may leave both boxes unchecked and still complete this form. Consent is not required
        for enrollment or services. See our{' '}
        <Link component={RouterLink} to="/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </Link>
        {' '}and{' '}
        <Link component={RouterLink} to="/messaging-terms" target="_blank" rel="noopener noreferrer">
          Messaging Terms
        </Link>
        .
      </Typography>
      <FormControlLabel
        sx={{ alignItems: 'flex-start', mr: 0, mb: 1 }}
        control={
          <Checkbox
            checked={!!fields.sms_consent}
            onChange={(e) => setF('sms_consent', e.target.checked)}
            sx={{ pt: 0.25 }}
          />
        }
        label={<Typography variant="body2">{SMS_CONSENT_LABEL}</Typography>}
      />
      <FormControlLabel
        sx={{ alignItems: 'flex-start', mr: 0, mb: 1.5 }}
        control={
          <Checkbox
            checked={!!fields.voice_consent}
            onChange={(e) => setF('voice_consent', e.target.checked)}
            sx={{ pt: 0.25 }}
          />
        }
        label={<Typography variant="body2">{VOICE_CONSENT_LABEL}</Typography>}
      />
      <Typography variant="body2" color="text.secondary">
        {ALTERNATE_CHANNEL_NOTICE}
      </Typography>
    </Box>
  );
}
